import React, { useCallback, useEffect, useState } from 'react';

import type { FontSizeFormat } from '@aline/common';
import { fontSizeDisplayNumbers } from '@aline/common';
import { MenuItem } from '@mui/material';
import { findIndex } from 'ramda';
import { AlineTooltip, SelectText } from 'src/components';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { ClientTransforms } from 'src/editor/transforms';
import { useCurrentUserId } from 'src/gql';
import { useAuthorizedTransforms } from 'src/hooks';
import { ReactEditor, useEditor, useEditorLoading } from 'src/layout-engine';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  gutters: {
    paddingLeft: 12,
    paddingRight: 12,
  },
  root: {
    fontSize: 14,
  },
}));

export const fontSizeSelectAttribute = 'toolbar-button-font-size';
export const fontSizeInputAttribute = 'toolbar-font-size-input';
export function fontSizeMenuItemAttribute(index: number): string {
  return `font-size-item-${index}`;
}

export const FontSizeSelect = React.memo(function FontSizeSelect(): JSX.Element {
  const { classes } = useStyles();
  const editor = useEditor();
  const editorLoading = useEditorLoading();
  const currentUserId = useCurrentUserId();
  const editorFocused = ReactEditor.isFocused(editor);
  const [inputValue, setInput] = useState<string>('');
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const [tooltipOpen, setTooltipOpen] = useState<boolean>(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [inputElement, setInputElement] = useState<HTMLInputElement | null>(null);
  const fontSize = useEditorTabStateSelector(state => state.derived.fontProperties.fontSize);
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);
  const inputFocused = document.activeElement === inputElement;

  /**
   * We wrap in useCallback so that we can use this function inside the useEffect below, and elsewhere.
   * See e.g., https://reactjs.org/docs/hooks-faq.html#is-it-safe-to-omit-functions-from-the-list-of-dependencies.
   */
  const handleFontSizeChange = useCallback(
    (fontSize: string): void => {
      function findFontIndex(string: string): number {
        return findIndex(fontSize => fontSize === string)(fontSizeDisplayNumbers);
      }

      const fontIndex = findFontIndex(fontSize);

      setSelectedIndex(fontIndex !== -1 ? fontIndex : null);
      setInput(fontSize);
    },
    [setSelectedIndex, setInput]
  );

  useEffect(() => {
    handleFontSizeChange(fontSize);
  }, [fontSize, handleFontSizeChange]);

  function applyFontSize(fontSizeValue: string): void {
    if (!currentUserId || !editingMode) return;

    const size = parseFloat(fontSizeValue);

    // We dont' worry about normalizing the font size here as it is normalized in AlineTransforms.changeFontSize.
    const newFontSize: FontSizeFormat = {
      origin: 'USER_APPLIED',
      size,
    };

    AuthorizedTransforms.changeFontSize(editor, {
      authorId: currentUserId,
      editingMode,
      newFontSize,
    });
    ClientTransforms.focus(editor);
  }

  function handleSelected(index: number, fontSizeItem: string): void {
    setSelectedIndex(index);
    setInput(fontSizeItem);
    setMenuOpen(false);
  }

  function handleSelectTextChange({
    target: { value },
  }: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
    handleFontSizeChange(value);
  }

  function handleMenuItemClick(index: number, fontSizeValue: string): void {
    handleSelected(index, fontSizeValue);
    applyFontSize(fontSizeValue);
  }

  function handleMenuOpen(): void {
    setTooltipOpen(false);
    setMenuOpen(true);
  }

  function handleMenuClose(): void {
    setMenuOpen(false);
  }

  function handleTooltipOpen(): void {
    setTooltipOpen(true);
  }

  function handleTooltipClose(): void {
    setTooltipOpen(false);
  }

  return (
    <AlineTooltip
      arrow
      open={tooltipOpen && !menuOpen && !inputFocused}
      placement="bottom"
      title="Font size"
      onClose={handleTooltipClose}
      onOpen={handleTooltipOpen}
    >
      <SelectText
        buttonAttribute={fontSizeSelectAttribute}
        disabled={editorLoading}
        divStyles={{
          height: 24,
          width: 75,
        }}
        editorAction={applyFontSize}
        iconStyles={{
          height: 12,
          right: 8,
          width: 12,
        }}
        inputAttribute={fontSizeInputAttribute}
        inputDOMElement={inputElement}
        inputRef={setInputElement}
        open={menuOpen}
        value={editorFocused ? fontSize : inputValue}
        onChange={handleSelectTextChange}
        onClose={handleMenuClose}
        onFocusInput={(): void => {
          setTooltipOpen(false);
        }}
        onMouseOutInputWrapper={(): void => {
          setTooltipOpen(false);
        }}
        onMouseOverInputWrapper={(): void => {
          setTooltipOpen(true);
        }}
        onOpen={handleMenuOpen}
      >
        {fontSizeDisplayNumbers.map((fontSizeValue, index) => (
          <MenuItem
            key={fontSizeValue}
            dense
            classes={{
              gutters: classes.gutters,
              root: classes.root,
            }}
            data-cy={fontSizeMenuItemAttribute(index)}
            selected={index === selectedIndex}
            onClick={(): void => {
              handleMenuItemClick(index, fontSizeValue);
            }}
          >
            {fontSizeValue}
          </MenuItem>
        ))}
      </SelectText>
    </AlineTooltip>
  );
});
